import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API, useAuth } from '../contexts/AuthContext';
import { LoadingScreen } from '../components/LoadingScreen';
import { useAutoSave, AutoSaveIndicator } from '../hooks/useAutoSave';
import CreateClientAccessModal from '../components/CreateClientAccessModal';
import AudioTranscriber from '../components/AudioTranscriber';
import BankScenariosWidget from '../components/BankScenariosWidget';
import ContractsSection from '../components/ContractsSection';
import ClientWorkspaceSection from '../components/ClientWorkspaceSection';
import CourtierClientInvitePanel from '../components/CourtierClientInvitePanel';
import { CreditsList, ChargesList, RentalsList, ExtraAssociates } from '../components/QualificationFields';

const QualificationPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  useEffect(() => {
    axios.get(`${API}/crm/leads`).then(r => setLeads(r.data.filter(l => l.status !== 'refuse'))).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const statusLabel = { nouveau: 'Nouveau', a_qualifier: 'A qualifier', en_cours: 'En cours', accepte: 'Accepte' };
  const q = search.trim().toLowerCase();
  const filtered = !q ? leads : leads.filter(l =>
    `${l.first_name || ''} ${l.last_name || ''}`.toLowerCase().includes(q) ||
    (l.email || '').toLowerCase().includes(q) ||
    (l.phone || '').replace(/\s/g, '').includes(q.replace(/\s/g, ''))
  );
  return (
    <div className="p-4 lg:p-8" data-testid="qualification-page">
      <div className="mb-6"><h1 className="text-3xl font-bold text-gray-900">Qualification Clients</h1><p className="text-gray-600 mt-1">Analysez et scorez les prospects</p></div>
      <div className="relative mb-6 max-w-md">
        <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Rechercher un client (nom, email, téléphone)…"
               className="realease-input w-full pl-10"
               data-testid="qualification-search-input" />
      </div>
      {loading ? <div className="text-center py-8"><div className="w-8 h-8 border-4 border-t-[#008A8D] border-gray-200 rounded-full animate-spin mx-auto"></div></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(lead => (
            <a key={lead.lead_id} href={`/qualification/${lead.lead_id}`} className="realease-card p-6 hover-lift border-l-4 border-[#008A8D]/30" data-testid={`lead-card-${lead.lead_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div><h3 className="font-semibold text-gray-900">{lead.first_name} {lead.last_name}</h3><p className="text-sm text-gray-500">{lead.email}</p></div>
                <span className={`status-badge status-${lead.status}`}>{statusLabel[lead.status]}</span>
              </div>
              {lead.phone && <p className="text-sm text-gray-600 mb-1">{lead.phone}</p>}
              <div className="mt-3 pt-3 border-t border-gray-100"><span className="text-[#008A8D] font-medium text-sm">Voir la qualification &rarr;</span></div>
            </a>
          ))}
          {filtered.length === 0 && <div className="col-span-full text-center py-12 text-gray-500">{q ? `Aucun client trouvé pour « ${search} ».` : 'Aucun lead a qualifier.'}</div>}
        </div>
      )}
    </div>
  );
};

// ==================== QUALIFICATION DETAIL ====================
const QualificationDetailPage = () => {
  const [lead, setLead] = useState(null);
  const [qual, setQual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [notes, setNotes] = useState('');
  const [aiSummary, setAiSummary] = useState('');
  const [genAi, setGenAi] = useState(false);
  const [prefillInfo, setPrefillInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const leadId = window.location.pathname.split('/').pop();

  useEffect(() => {
    const load = async () => {
      try {
        const lr = await axios.get(`${API}/crm/leads/${leadId}`);
        setLead(lr.data);
        try {
          const qr = await axios.get(`${API}/qualification/qualifications/${leadId}`);
          setQual(qr.data); setForm(qr.data.data || {}); setNotes(qr.data.exchange_notes || ''); setAiSummary(qr.data.ai_summary || '');
        } catch {
          // Pas de qualification -> appelle le prefill CRM+prequal
          try {
            const pr = await axios.get(`${API}/qualification/prefill/${leadId}`);
            if (pr.data?.prefilled && pr.data?.data) {
              setForm(pr.data.data);
              setPrefillInfo({ source: pr.data.source, prequal_id: pr.data.prequal_id });
            }
          } catch (_) { /* ignore */ }
        }
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || '';
        if (status === 403) {
          setLoadError({ code: 403, msg: detail || 'Ce prospect appartient a une autre branche RealEase.' });
        } else if (status === 404) {
          setLoadError({ code: 404, msg: 'Ce prospect n\'existe pas ou a ete supprime.' });
        } else {
          setLoadError({ code: status || 0, msg: detail || 'Erreur de chargement du prospect.' });
        }
      } finally { setLoading(false); }
    };
    load();
  }, [leadId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (qual) { await axios.put(`${API}/qualification/qualifications/${qual.qualification_id}`, { data: form, exchange_notes: notes }); }
      else { const r = await axios.post(`${API}/qualification/qualifications`, { lead_id: leadId, data: form, exchange_notes: notes }); setQual(r.data); }
      const qr = await axios.get(`${API}/qualification/qualifications/${leadId}`);
      setQual(qr.data);
    } catch (e) { console.error(e); } finally { setSaving(false); }
  };

  // Auto-save : declenche apres chaque modif du form ou des notes (debounce 800ms)
  const autoState = React.useMemo(() => ({ data: form, exchange_notes: notes, _lid: leadId }), [form, notes, leadId]);
  const auto = useAutoSave({
    state: autoState,
    enabled: !loading && Object.keys(form).length > 0,
    canCreate: () => !!leadId && !qual,
    createFn: async (s) => {
      const r = await axios.post(`${API}/qualification/qualifications`, { lead_id: leadId, data: s.data, exchange_notes: s.exchange_notes });
      setQual(r.data);
      return { id: r.data.qualification_id, doc: r.data };
    },
    updateFn: async (id, s) => {
      await axios.put(`${API}/qualification/qualifications/${id}`, { data: s.data, exchange_notes: s.exchange_notes });
    },
    debounceMs: 800,
  });

  // Synchronise le recordId quand la qualification existe deja en base
  React.useEffect(() => {
    if (qual?.qualification_id && !auto.recordId) auto.setRecordId(qual.qualification_id);
  }, [qual, auto]);

  const handleDecision = async (decision) => {
    if (!qual) return;
    try {
      await axios.post(`${API}/qualification/qualifications/${qual.qualification_id}/decision`, { decision });
      setQual({ ...qual, decision });
    } catch (e) { console.error(e); }
  };

  const genSummary = async () => {
    if (!qual || !notes) return;
    setGenAi(true);
    try { const r = await axios.post(`${API}/qualification/qualifications/${qual.qualification_id}/ai-summary`); setAiSummary(r.data.ai_summary); }
    catch (e) { console.error(e); } finally { setGenAi(false); }
  };

  const up = (k, v) => setForm({ ...form, [k]: v });
  const [showClientAccessModal, setShowClientAccessModal] = useState(false);

  if (loading) return <LoadingScreen />;
  if (!lead) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className={`rounded-lg p-6 border ${loadError?.code === 403 ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-gray-50 border-gray-200 text-gray-700'}`} data-testid="lead-load-error">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.75-2.98l-7.07-12.24a2 2 0 00-3.5 0L3.18 16.02A2 2 0 004.93 19z" />
            </svg>
            <div>
              <h2 className="text-lg font-semibold mb-1">
                {loadError?.code === 403 ? 'Acces restreint' : 'Prospect introuvable'}
              </h2>
              <p className="text-sm">{loadError?.msg || 'Ce prospect n\'existe pas.'}</p>
              {loadError?.code === 403 && (
                <p className="text-xs mt-2 text-amber-800">
                  Ce prospect est rattache a une autre branche. Contactez un CEO ou administrateur pour le re-assigner a votre branche si besoin.
                </p>
              )}
              <a href="/qualification" className="inline-block mt-4 text-[#008A8D] hover:underline text-sm font-medium">&larr; Retour aux qualifications</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8" data-testid="qualification-detail">
      <div className="flex items-center justify-between mb-8">
        <div>
          <a href="/qualification" className="text-[#008A8D] hover:underline text-sm mb-2 inline-block">&larr; Retour</a>
          <h1 className="text-3xl font-bold text-gray-900">{lead.first_name} {lead.last_name}</h1>
          <p className="text-gray-600">{lead.email} {lead.phone && `| ${lead.phone}`}</p>
          <p className="text-gray-500 text-sm mt-0.5" data-testid="lead-address">
            📍 {lead.address ? `${lead.address}${lead.postal_code ? `, ${lead.postal_code}` : ''}${lead.city ? ` ${lead.city}` : ''}` : <span className="italic">Adresse non renseignée</span>}
            <button onClick={async () => {
              const addr = window.prompt('Adresse postale du client :', lead.address || '');
              if (addr === null) return;
              try {
                await axios.put(`${API}/crm/leads/${lead.lead_id}`, { address: addr });
                setLead({ ...lead, address: addr });
              } catch (e) { alert('Erreur lors de la mise à jour de l\u2019adresse'); }
            }} className="ml-2 text-[#008A8D] text-xs underline hover:text-[#006B6D]" data-testid="edit-address-btn">modifier</button>
          </p>
          <BranchSelector lead={lead} onChanged={b => setLead({ ...lead, branch: b })} />
          <div className="mt-2"><AutoSaveIndicator status={auto.status} savedAt={auto.savedAt} error={auto.error} /></div>
        </div>
        <div className="flex items-center gap-2">
          {qual && (
            <button onClick={async () => {
              try {
                await handleSave();
                const r = await axios.get(`${API}/qualification/qualifications/${qual.qualification_id}/courtier-pdf?_=${Date.now()}`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `RealEase_Dossier_Courtier_${(lead.first_name || '')}_${(lead.last_name || '')}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) { alert('Erreur génération du dossier courtier'); }
            }}
              className="px-3 py-2 bg-[#13736D] text-white rounded-lg text-sm font-semibold hover:bg-[#0F5C57] flex items-center gap-1.5"
              title="Dossier PDF complet (revenus, credits, charges, epargne, simulations, scoring) a transmettre au courtier"
              data-testid="courtier-pdf-btn">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              Dossier courtier
            </button>
          )}
          {qual && (
            <button onClick={async () => {
              if (!window.confirm(`Supprimer la qualification de ${lead.first_name} ${lead.last_name} ?\n\nLe prospect reste dans le CRM, mais toutes les données de qualification (situation, revenus, simulations, notes) seront définitivement effacées.`)) return;
              try {
                await axios.delete(`${API}/qualification/qualifications/${qual.qualification_id}`);
                window.location.href = '/qualification';
              } catch (e) { alert(e.response?.data?.detail || 'Erreur lors de la suppression'); }
            }}
              className="px-3 py-2 border border-red-300 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-50"
              title="Supprimer la qualification (le prospect reste dans le CRM)"
              data-testid="delete-qualification-btn">
              🗑 Supprimer
            </button>
          )}
          {!lead.client_user_id ? (
            <button onClick={() => setShowClientAccessModal(true)}
                    className="px-4 py-2 bg-[#A57F4F] text-white rounded-lg font-semibold hover:bg-[#8B6A40] flex items-center gap-2"
                    data-testid="create-client-access-btn">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7zM18 8v6m3-3h-6" /></svg>
              Creer un acces client
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 text-xs font-semibold rounded-lg flex items-center gap-1.5" data-testid="client-access-active">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                Acces client actif
              </span>
              <button onClick={async () => {
                if (!window.confirm("Reinviter le client ? Un NOUVEAU mot de passe lui sera envoye par email (remplace l'ancien).")) return;
                try {
                  const userId = lead.client_user_id;
                  const r = await axios.post(`${API}/admin/client-access/${userId}/resend`);
                  alert(`Email renvoye avec succes a ${r.data.email}.\nNouveau mot de passe : ${r.data.password}\n\nGarde-le precieusement ou communique-le au client.`);
                } catch (e) {
                  alert(e.response?.data?.detail || 'Erreur re-invitation');
                }
              }}
                      className="px-3 py-1.5 text-xs border border-[#008A8D] text-[#008A8D] rounded-lg font-semibold hover:bg-[#008A8D]/5"
                      data-testid="reinvite-client-btn">
                Reinviter (nouveau mot de passe)
              </button>
            </div>
          )}
        </div>
      </div>
      <CreateClientAccessModal open={showClientAccessModal}
                               onClose={() => setShowClientAccessModal(false)}
                               defaultEmail={lead.email}
                               defaultName={`${lead.first_name || ''} ${lead.last_name || ''}`.trim()}
                               leadId={leadId}
                               onCreated={() => { setLead({...lead, client_user_id: 'created'}); }} />

      {/* Bandeau "donnees pre-remplies" si prefill depuis prequal/CRM */}
      {prefillInfo && !qual && (
        <div className="mb-6 p-4 bg-gradient-to-r from-teal-50 to-amber-50 border-l-4 border-[#008A8D] rounded-lg flex items-start gap-3" data-testid="prefill-banner">
          <svg className="w-5 h-5 text-[#008A8D] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-800">
              Donnees pre-remplies depuis {prefillInfo.source === 'prequalification' ? 'la pre-qualification client' : 'le CRM'}
            </p>
            <p className="text-xs text-gray-600 mt-0.5">Verifiez et completez les champs ci-dessous, puis cliquez sur "Sauvegarder" pour creer la qualification.</p>
          </div>
          <button onClick={() => setPrefillInfo(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none" title="Fermer">&times;</button>
        </div>
      )}

      {/* Espace de travail client unifié */}
      <div className="mb-8">
        <ClientWorkspaceSection leadId={leadId} />
      </div>

      {/* Acces courtier (Alexandre / Artemis) sur ce client - avant projet */}
      <div className="mb-8">
        <CourtierClientInvitePanel leadId={leadId} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <FormSection title="Situation personnelle">
            <div className="grid grid-cols-2 gap-4">
              <FField label="Age" type="number" value={form.age} onChange={v => up('age', parseInt(v) || null)} />
              <FField label="Situation familiale" type="select" value={form.family_situation} onChange={v => up('family_situation', v)} opts={[{v:'',l:'Selectionner'},{v:'celibataire',l:'Celibataire'},{v:'marie',l:'Marie(e)'},{v:'pacse',l:'Pacse(e)'},{v:'divorce',l:'Divorce(e)'}]} />
              <FField label="Situation professionnelle" type="select" value={form.professional_situation} onChange={v => up('professional_situation', v)} opts={[{v:'',l:'Selectionner'},{v:'cdi',l:'CDI'},{v:'fonctionnaire',l:'Fonctionnaire'},{v:'cdd',l:'CDD'},{v:'interim',l:'Interim / Mission'},{v:'independant',l:'Independant / Liberal'},{v:'gerant',l:'Gerant / Chef d\'entreprise'},{v:'retraite',l:'Retraite(e)'},{v:'precaire',l:'Precaire / Sans emploi'}]} />
              <FField label="Anciennete (annees)" type="number" value={form.seniority_years} onChange={v => up('seniority_years', parseInt(v) || null)} />
              <FField label="Nombre d'enfants a charge" type="number" value={form.children_count} onChange={v => up('children_count', parseInt(v) || 0)} />
              {/* Residence fiscale - impact fort sur faisabilite bancaire */}
              <div className="col-span-2">
                <label className="text-xs font-semibold text-gray-700 block mb-2 uppercase tracking-wide">Residence fiscale</label>
                <div className="grid grid-cols-2 gap-2">
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${(form.fiscal_residence || 'fr') === 'fr' ? 'border-[#008A8D] bg-[#008A8D]/5' : 'border-gray-200 hover:border-gray-300'}`} data-testid="fiscal-residence-fr">
                    <input type="radio" name="fiscal_residence" checked={(form.fiscal_residence || 'fr') === 'fr'} onChange={() => up('fiscal_residence', 'fr')} className="w-4 h-4 text-[#008A8D]" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Resident fiscal francais</p>
                      <p className="text-xs text-gray-500">Acces normal a toutes les banques</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${form.fiscal_residence === 'non_fr' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`} data-testid="fiscal-residence-non-fr">
                    <input type="radio" name="fiscal_residence" checked={form.fiscal_residence === 'non_fr'} onChange={() => up('fiscal_residence', 'non_fr')} className="w-4 h-4 text-amber-600" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Non resident fiscal francais</p>
                      <p className="text-xs text-amber-700">Banques limitees, apport majore (souvent 20-30%)</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </FormSection>

          {/* Investisseurs 1 et 2 cote a cote (gauche / droite) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Investisseur 1 */}
            <div className="realease-card p-5 space-y-5 border-l-4 border-l-[#008A8D]" data-testid="investor-1-card">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Investisseur 1</h3>
                <span className="text-xs px-2 py-0.5 bg-[#008A8D]/10 text-[#006B6E] rounded-full font-semibold">Principal</span>
              </div>
              {/* 1. Revenus (salaire) */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#008A8D] text-white text-[10px] font-bold flex items-center justify-center">1</span>
                  Revenus (salaire)
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <FField label="Revenu net fiscal mensuel (EUR)" type="number" value={form.monthly_net_income} onChange={v => up('monthly_net_income', parseFloat(v) || null)} />
                  <FField label="Revenu fiscal de reference (EUR/an)" type="number" value={form.annual_reference_income} onChange={v => up('annual_reference_income', parseFloat(v) || null)} testid="annual-reference-income" />
                  <FField label="Stabilite des revenus" type="select" value={form.income_stability} onChange={v => up('income_stability', v)} opts={[{v:'',l:'Selectionner'},{v:'cdi',l:'CDI'},{v:'fonctionnaire',l:'Fonctionnaire'},{v:'cdd',l:'CDD'},{v:'interim',l:'Interim / Mission'},{v:'independant',l:'Independant / Liberal'},{v:'gerant',l:'Gerant / Chef d\'entreprise'},{v:'precaire',l:'Precaire / Sans emploi'}]} />
                </div>
              </div>
              {/* 2. Credits en cours */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#008A8D] text-white text-[10px] font-bold flex items-center justify-center">2</span>
                  Credits en cours
                </h4>
                <CreditsList credits={form.credits || []} onChange={v => up('credits', v)} />
              </div>
              {/* 3. Charges (pensions alimentaires etc.) */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#008A8D] text-white text-[10px] font-bold flex items-center justify-center">3</span>
                  Charges (pension alimentaire, etc.)
                </h4>
                <ChargesList charges={form.charges_list || []} onChange={v => up('charges_list', v)} />
              </div>
              {/* 4. Epargne / apport */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#008A8D] text-white text-[10px] font-bold flex items-center justify-center">4</span>
                  Epargne / apport disponible
                </h4>
                <SavingsList savings={form.savings_list || []} onChange={v => up('savings_list', v)} />
                <div className="mt-3">
                  <FField label="Apport disponible pour ce projet (EUR)" type="number" value={form.investor1_project_apport} onChange={v => up('investor1_project_apport', parseFloat(v) || null)} />
                </div>
              </div>
              {/* 5. Revenus locatifs (en dernier) avec toggle ponderation mis en valeur */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#008A8D] text-white text-[10px] font-bold flex items-center justify-center">5</span>
                  Revenus locatifs (biens existants)
                </h4>
                <RentalsList rentals={form.rentals_list || []} onChange={v => up('rentals_list', v)} />
                {/* Toggle ponderation mis en avant - plus grand, plus lisible, fond amber */}
                <label className="mt-3 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors" data-testid="ponderate-rental-inv1">
                  <input type="checkbox" checked={form.ponderate_rental !== false} onChange={e => up('ponderate_rental', e.target.checked)} className="w-5 h-5 rounded text-[#008A8D] mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-900">Ponderer les revenus locatifs a 80 % (methodologie HCSF)</p>
                    <p className="text-xs text-amber-700 mt-0.5">Les banques retiennent generalement 70-80 % des loyers percus. Decochez seulement si votre banque applique un pourcentage different.</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Investisseur 2 */}
            <div className={`realease-card p-5 space-y-5 ${form.has_co_borrower ? 'border-l-4 border-l-[#A57F4F]' : 'border-l-4 border-l-gray-200 bg-gray-50/50'}`} data-testid="investor-2-card">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Investisseur 2</h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.has_co_borrower || false} onChange={e => up('has_co_borrower', e.target.checked)} className="w-4 h-4 rounded text-[#008A8D]" data-testid="has-co-borrower" />
                  <span className="text-xs font-semibold text-gray-700">{form.has_co_borrower ? 'Active' : 'Activer'}</span>
                </label>
              </div>
              {!form.has_co_borrower ? (
                <p className="text-sm text-gray-400 italic text-center py-12">Cochez "Activer" si l'investissement se fait a deux (conjoint, associe, indivision...).</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <FField label="Nom de l'investisseur 2" type="text" value={form.co_borrower_name} onChange={v => up('co_borrower_name', v)} />
                    <FField label="Lien (conjoint, associe...)" type="text" value={form.co_borrower_relation} onChange={v => up('co_borrower_relation', v)} />
                  </div>
                  {/* 1. Revenus */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-[#A57F4F] text-white text-[10px] font-bold flex items-center justify-center">1</span>
                      Revenus (salaire)
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <FField label="Revenu net fiscal mensuel (EUR)" type="number" value={form.co_borrower_income} onChange={v => up('co_borrower_income', parseFloat(v) || null)} />
                      <FField label="Revenu fiscal de reference (EUR/an)" type="number" value={form.co_borrower_annual_reference_income} onChange={v => up('co_borrower_annual_reference_income', parseFloat(v) || null)} testid="co-annual-reference-income" />
                      <FField label="Stabilite des revenus" type="select" value={form.co_borrower_stability} onChange={v => up('co_borrower_stability', v)} opts={[{v:'',l:'Selectionner'},{v:'cdi',l:'CDI'},{v:'fonctionnaire',l:'Fonctionnaire'},{v:'cdd',l:'CDD'},{v:'interim',l:'Interim / Mission'},{v:'independant',l:'Independant / Liberal'},{v:'gerant',l:'Gerant / Chef d\'entreprise'},{v:'precaire',l:'Precaire / Sans emploi'}]} />
                    </div>
                  </div>
                  {/* 2. Credits */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-[#A57F4F] text-white text-[10px] font-bold flex items-center justify-center">2</span>
                      Credits en cours
                    </h4>
                    <CreditsList credits={form.co_borrower_credits_list || []} onChange={v => up('co_borrower_credits_list', v)} />
                  </div>
                  {/* 3. Charges */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-[#A57F4F] text-white text-[10px] font-bold flex items-center justify-center">3</span>
                      Charges (pension alimentaire, etc.)
                    </h4>
                    <ChargesList charges={form.co_borrower_charges_list || []} onChange={v => up('co_borrower_charges_list', v)} />
                  </div>
                  {/* 4. Epargne */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-[#A57F4F] text-white text-[10px] font-bold flex items-center justify-center">4</span>
                      Epargne / apport disponible
                    </h4>
                    <SavingsList savings={form.co_borrower_savings_list || []} onChange={v => up('co_borrower_savings_list', v)} />
                    <div className="mt-3">
                      <FField label="Apport disponible pour ce projet (EUR)" type="number" value={form.investor2_project_apport} onChange={v => up('investor2_project_apport', parseFloat(v) || null)} />
                    </div>
                  </div>
                  {/* 5. Revenus locatifs */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-[#A57F4F] text-white text-[10px] font-bold flex items-center justify-center">5</span>
                      Revenus locatifs (biens existants)
                    </h4>
                    <RentalsList rentals={form.co_borrower_rentals_list || []} onChange={v => up('co_borrower_rentals_list', v)} />
                    <label className="mt-3 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors" data-testid="ponderate-rental-inv2">
                      <input type="checkbox" checked={form.co_borrower_ponderate_rental !== false} onChange={e => up('co_borrower_ponderate_rental', e.target.checked)} className="w-5 h-5 rounded text-[#A57F4F] mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-amber-900">Ponderer les revenus locatifs a 80 % (methodologie HCSF)</p>
                        <p className="text-xs text-amber-700 mt-0.5">Les banques retiennent generalement 70-80 % des loyers percus.</p>
                      </div>
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Investisseurs supplementaires (3, 4, ...) */}
          <FormSection title="Investisseurs supplementaires (SCI, indivision...)">
            <ExtraAssociates list={form.extra_associates || []} onChange={v => up('extra_associates', v)} />
          </FormSection>

          {/* Synthese endettement consolidee */}
          <FormSection title="Synthese endettement avant projet (investisseur 1 + investisseur 2 + autres)">
            <AutoDebtRatio form={form} />
          </FormSection>

          {/* Capacite d'emprunt sur scenarios bancaires references */}
          <FormSection title="Capacite d'emprunt - scenarios references (300k / 350k / 400k)">
            <BankScenariosWidget form={form} />
          </FormSection>

          <FormSection title="Patrimoine immobilier">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 col-span-2">
                <input type="checkbox" checked={form.has_primary_residence || false} onChange={e => up('has_primary_residence', e.target.checked)} className="w-5 h-5 rounded text-[#008A8D]" />
                <label className="text-sm text-gray-700">Proprietaire residence principale</label>
              </div>
              <FField label="Nombre de biens immobiliers" type="number" value={form.real_estate_assets} onChange={v => up('real_estate_assets', parseInt(v) || null)} />
            </div>
          </FormSection>

          <FormSection title="Projet d'investissement locatif">
            <div className="grid grid-cols-2 gap-4">
              <FField label="Budget recherche (EUR)" type="number" value={form.target_budget} onChange={v => up('target_budget', parseFloat(v) || null)} />
              <FField label="Zone geographique" type="text" value={form.geographic_zone} onChange={v => up('geographic_zone', v)} />
              <FField label="Horizon" type="select" value={form.horizon} onChange={v => up('horizon', v)} opts={[{v:'',l:'Selectionner'},{v:'court',l:'Court terme (< 6 mois)'},{v:'moyen',l:'Moyen terme (6-12 mois)'},{v:'long',l:'Long terme (> 12 mois)'}]} />
              <FField label="Objectif" type="select" value={form.objective} onChange={v => up('objective', v)} opts={[{v:'',l:'Selectionner'},{v:'patrimoine',l:'Constituer un patrimoine'},{v:'revenus',l:'Generer des revenus'},{v:'defiscalisation',l:'Defiscalisation'}]} />
            </div>
          </FormSection>

          {/* Multi-simulations endettement futur */}
          <FormSection title="Simulations de projets">
            <p className="text-sm text-gray-500 mb-4">Comparez plusieurs scenarios pour trouver le meilleur montage</p>
            <MultiSimulations form={form} simulations={form.simulations || []} onChange={v => up('simulations', v)} />
          </FormSection>
          <FormSection title="Notes d'echange">
            <div className="mb-3 p-3 bg-[#FDF8F0] border border-[#E8DCC4] rounded-lg flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-gray-800">Notes vocales (transcription auto)</p>
                <p className="text-xs text-gray-500">Chargez un memo WhatsApp ou enregistrez un appel : Whisper transcrit + Claude genere automatiquement le resume IA et les questions a creuser.</p>
              </div>
              <AudioTranscriber onTranscribed={async (text) => {
                const newNotes = (notes ? notes + "\n\n" : "") + text;
                setNotes(newNotes);
                // Save then auto-generate AI summary
                try {
                  setGenAi(true);
                  let q = qual;
                  if (q) {
                    await axios.put(`${API}/qualification/qualifications/${q.qualification_id}`, { data: form, exchange_notes: newNotes });
                  } else {
                    const r = await axios.post(`${API}/qualification/qualifications`, { lead_id: leadId, data: form, exchange_notes: newNotes });
                    q = r.data; setQual(r.data);
                  }
                  const ai = await axios.post(`${API}/qualification/qualifications/${q.qualification_id}/ai-summary`);
                  setAiSummary(ai.data.ai_summary);
                } catch (e) { console.error(e); }
                finally { setGenAi(false); }
              }} />
            </div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="realease-input h-40 resize-none" placeholder="Resumez ici les echanges avec le client..." data-testid="exchange-notes" />
            {notes && qual && (
              <button onClick={genSummary} disabled={genAi} className="mt-3 realease-btn-secondary text-sm" data-testid="generate-ai-summary">
                {genAi ? 'Generation en cours...' : 'Generer un resume IA'}
              </button>
            )}
            {aiSummary && (
              <div className="mt-4 p-4 bg-[#008A8D]/5 rounded-lg border border-[#008A8D]/20">
                <h4 className="font-semibold text-[#008A8D] mb-2">Resume IA</h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{aiSummary}</p>
              </div>
            )}
          </FormSection>
        </div>
        <div className="space-y-6">
          {/* Bloc-notes rapide sticky - pour retranscrire les infos a la volee pendant l'appel */}
          <div className="realease-card p-4 sticky top-4 border-l-4 border-l-amber-400 bg-gradient-to-br from-amber-50 to-white" data-testid="quick-notes-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                <h3 className="font-semibold text-gray-900 text-sm">Bloc-notes rapide</h3>
              </div>
              <span className="text-[10px] text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full font-semibold">Prise de notes</span>
            </div>
            <p className="text-[11px] text-gray-500 mb-2">Zone libre pour noter les infos-cles pendant l'appel. Sauvegarde automatique avec le reste.</p>
            <textarea value={form.quick_notes || ''}
                      onChange={e => up('quick_notes', e.target.value)}
                      className="w-full h-64 p-3 text-sm border border-amber-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none font-mono"
                      placeholder="• Infos entretien&#10;• Objections&#10;• Points suivre&#10;• Rappels..."
                      data-testid="quick-notes-textarea" />
            <button onClick={handleSave}
                    disabled={saving}
                    className="mt-2 w-full px-3 py-1.5 text-xs bg-amber-500 text-white rounded font-semibold hover:bg-amber-600 disabled:opacity-50"
                    data-testid="save-quick-notes">
              {saving ? 'Sauvegarde...' : 'Sauvegarder notes'}
            </button>
          </div>
          <ContractsSection leadId={leadId} leadName={`${lead.first_name || ''} ${lead.last_name || ''}`.trim()} />
          {qual?.scores && (
            <div className="realease-card p-6" data-testid="scores-panel">
              <h3 className="font-semibold text-gray-900 mb-4">Scoring</h3>
              <div className="space-y-4">
                <ScoreBar label="Capacite" score={qual.scores.capacity_score} />
                <ScoreBar label="Apport" score={qual.scores.contribution_score} />
                <ScoreBar label="Stabilite" score={qual.scores.stability_score} />
                <ScoreBar label="Endettement" score={qual.scores.debt_score} />
                <ScoreBar label="Projet" score={qual.scores.project_score} />
                <div className="pt-4 border-t"><div className="flex justify-between"><span className="font-semibold">Score Global</span>
                  <span className={`text-2xl font-bold ${qual.scores.global_score >= 70 ? 'text-green-600' : qual.scores.global_score >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>{Math.round(qual.scores.global_score)}/100</span>
                </div></div>
              </div>
            </div>
          )}
          {qual && (
            <div className="realease-card p-6" data-testid="analysis-panel">
              <h3 className="font-semibold text-gray-900 mb-4">Analyse</h3>
              {qual.strengths?.length > 0 && <div className="mb-3"><h4 className="text-sm font-medium text-green-700 mb-1">Points forts</h4>{qual.strengths.map((s,i) => <p key={i} className="text-sm text-gray-600 flex gap-1"><span className="text-green-500">&#10003;</span> {s}</p>)}</div>}
              {qual.weaknesses?.length > 0 && <div className="mb-3"><h4 className="text-sm font-medium text-red-700 mb-1">Points faibles</h4>{qual.weaknesses.map((w,i) => <p key={i} className="text-sm text-gray-600 flex gap-1"><span className="text-red-500">&#10007;</span> {w}</p>)}</div>}
              {qual.weak_signals?.length > 0 && <div><h4 className="text-sm font-medium text-yellow-700 mb-1">Signaux faibles</h4>{qual.weak_signals.map((s,i) => <p key={i} className="text-sm text-gray-600 flex gap-1"><span className="text-yellow-500">&#9888;</span> {s}</p>)}</div>}
            </div>
          )}
          {qual && (
            <div className="realease-card p-6" data-testid="decision-panel">
              <h3 className="font-semibold text-gray-900 mb-4">Decision</h3>
              <div className="space-y-3">
                {[{d:'accepte',l:'Accepter',c:'green'},{d:'en_attente',l:'En attente',c:'yellow'},{d:'refuse',l:'Refuser',c:'red'}].map(({d,l,c}) => (
                  <button key={d} onClick={() => handleDecision(d)} className={`w-full py-3 rounded-lg font-medium transition-colors ${qual.decision === d ? `bg-${c}-500 text-white` : `bg-${c}-50 text-${c}-700 hover:bg-${c}-100`}`} data-testid={`decision-${d}`}>{l}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bouton Valider en bas (sticky) */}
      <div className="mt-8 sticky bottom-4 flex justify-end">
        <button onClick={handleSave} disabled={saving}
                className="realease-btn-primary shadow-2xl px-6 py-3 flex items-center gap-2"
                data-testid="save-qualification-bottom">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          {saving ? 'Validation...' : 'Valider la qualification'}
        </button>
      </div>
    </div>
  );
};

const FormSection = ({ title, children }) => (<div className="realease-card p-6"><h3 className="font-semibold text-gray-900 mb-4">{title}</h3>{children}</div>);

// Savings list component
const SavingsList = ({ savings, onChange }) => {
  const list = savings.length > 0 ? savings : [{ type: 'Livret A', amount: '' }];
  const addSaving = () => onChange([...list, { type: 'Livret A', amount: '' }]);
  const removeSaving = (i) => onChange(list.filter((_, idx) => idx !== i));
  const updateSaving = (i, k, v) => { const s = [...list]; s[i] = { ...s[i], [k]: v }; onChange(s); };
  const types = ['Livret A', 'LDD', 'Livret Jeune', 'PEL', 'CEL', 'Assurance Vie', 'PER', 'PEA', 'CTO', 'SCPI', 'Crypto', 'Livret solidaire', 'Autre'];
  const total = list.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  return (
    <div>
      {list.map((s, i) => (
        <div key={i} className="grid grid-cols-3 gap-2 mb-2 items-end">
          <div><label className="text-xs text-gray-500">Type</label>
            <select value={types.includes(s.type) ? s.type : 'Autre'} onChange={e => updateSaving(i, 'type', e.target.value)} className="realease-input text-sm">
              {types.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {s.type === 'Autre' && <input type="text" value={s.custom_type || ''} onChange={e => updateSaving(i, 'custom_type', e.target.value)} className="realease-input text-sm mt-1" placeholder="Preciser..." />}
          </div>
          <div><label className="text-xs text-gray-500">Montant (EUR)</label><input type="number" value={s.amount || ''} onChange={e => updateSaving(i, 'amount', e.target.value)} className="realease-input text-sm" /></div>
          {list.length > 1 ? (
            <button onClick={() => removeSaving(i)} className="text-red-500 text-sm hover:underline pb-2">Retirer</button>
          ) : <span></span>}
        </div>
      ))}
      <div className="flex items-center justify-between mt-2">
        <button onClick={addSaving} className="text-[#008A8D] text-sm font-medium hover:underline">+ Ajouter une epargne</button>
        {total > 0 && <span className="text-sm font-medium text-gray-700">Epargne totale : {total.toLocaleString('fr-FR')} EUR</span>}
      </div>
    </div>
  );
};

// Selecteur de branche (CEO / cross-branch uniquement)
const BRANCH_LABELS = { bretagne: 'RealEase Bretagne', nord: 'RealEase Hauts-de-France' };
const BranchSelector = ({ lead, onChanged }) => {
  const { user } = useAuth();
  const [changing, setChanging] = useState(false);
  const current = lead.branch || 'bretagne';
  const canEdit = user?.cross_branch_access || user?.role === 'ceo';
  const change = async (branch) => {
    if (branch === current) return;
    const losing = !user?.cross_branch_access && branch !== (user?.branch || 'bretagne');
    const msg = `Transférer ${lead.first_name} ${lead.last_name} vers ${BRANCH_LABELS[branch]} ?\n\nLa qualification, les pré-qualifications et les projets liés suivront.${losing ? '\n\n⚠️ Vous perdrez l\u2019accès à ce client (hors de votre branche).' : ''}`;
    if (!window.confirm(msg)) return;
    setChanging(true);
    try {
      await axios.put(`${API}/crm/leads/${lead.lead_id}/branch`, { branch });
      onChanged(branch);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Erreur lors du changement de branche');
    } finally { setChanging(false); }
  };
  return (
    <div className="flex items-center gap-2 mt-2">
      <span className="text-xs text-gray-500">Branche :</span>
      {canEdit ? (
        <select value={current} onChange={e => change(e.target.value)} disabled={changing}
          className="text-xs border border-gray-300 rounded-lg px-2 py-1 font-semibold text-gray-700 bg-white focus:ring-1 focus:ring-[#008A8D] disabled:opacity-50"
          data-testid="lead-branch-select">
          <option value="bretagne">RealEase Bretagne</option>
          <option value="nord">RealEase Hauts-de-France</option>
        </select>
      ) : (
        <span className="text-xs font-semibold px-2 py-1 bg-gray-100 rounded-lg text-gray-700" data-testid="lead-branch-badge">{BRANCH_LABELS[current]}</span>
      )}
      {changing && <span className="text-xs text-gray-400">Transfert...</span>}
    </div>
  );
};

// Multi-simulations component
const MultiSimulations = ({ form, simulations, onChange }) => {
  const addSim = () => onChange([...simulations, { label: `Scenario ${simulations.length + 1}`, amount: '', apport: '', monthly: '', duration: '', rate: '', rent: '' }]);
  const removeSim = (i) => onChange(simulations.filter((_, idx) => idx !== i));
  // Mensualite auto (capital + interets, hors assurance) a partir de montant - apport, duree, taux
  const calcSimMonthly = (sim) => {
    const loan = Math.max((parseFloat(sim.amount) || 0) - (parseFloat(sim.apport) || 0), 0);
    const years = parseFloat(sim.duration) || 0;
    const rate = parseFloat(sim.rate) || 0;
    if (loan <= 0 || years <= 0) return null;
    const n = years * 12, r = rate / 100 / 12;
    const m = r === 0 ? loan / n : loan * r / (1 - Math.pow(1 + r, -n));
    return Math.round(m * 100) / 100;
  };
  const updateSim = (i, k, v) => {
    const s = [...simulations];
    const sim = { ...s[i], [k]: v };
    if (k === 'monthly') {
      sim.monthly_manual = v !== '' && v != null;
    } else if (['amount', 'apport', 'duration', 'rate'].includes(k) && !sim.monthly_manual) {
      const am = calcSimMonthly(sim);
      if (am != null) sim.monthly = String(am);
    }
    s[i] = sim;
    onChange(s);
  };

  // Current debt situation
  // Iter 112 : on inclut TOUJOURS les revenus et charges du co-emprunteur si
  // presents, meme si has_co_borrower n'est pas explicitement coche. Symetrique
  // avec le backend (routes/qualifications.py calculate_scores).
  const pond = form.ponderate_rental !== false ? 0.8 : 1;
  const rentalsListTotal = (form.rentals_list || []).reduce((s, r) => s + (parseFloat(r.rent) || 0), 0);
  const leadIncome = (parseFloat(form.monthly_net_income) || 0) + rentalsListTotal * pond;
  const coRentalsTotal = (form.co_borrower_rentals_list || []).reduce((s, r) => s + (parseFloat(r.rent) || 0), 0);
  const coIncome = (parseFloat(form.co_borrower_income) || 0) + coRentalsTotal * pond;
  const totalIncomeActuel = leadIncome + coIncome;
  const creditMonthly = (form.credits || []).reduce((s, c) => s + (parseFloat(c.monthly) || 0), 0);
  const chargesTotal = (form.charges_list || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const coCreditMonthly = (form.co_borrower_credits_list || []).reduce((s, c) => s + (parseFloat(c.monthly) || 0), 0);
  const coChargesTotal = (form.co_borrower_charges_list || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const totalCreditsActuel = creditMonthly + coCreditMonthly;
  const totalChargesActuel = totalCreditsActuel + chargesTotal + coChargesTotal;
  const debtActuel = totalIncomeActuel > 0 ? (totalChargesActuel / totalIncomeActuel * 100) : 0;
  const rvActuel = totalIncomeActuel - totalChargesActuel;

  const children = form.children_count || 0;
  const adults = form.has_co_borrower ? 2 : 1;
  const minRv = adults * 700 + children * 300;

  return (
    <div>
      {simulations.length === 0 && (
        <div className="text-center py-6 text-gray-400">
          <p className="mb-3">Aucun scenario de simulation</p>
        </div>
      )}

      {simulations.map((sim, i) => {
        const simMonthly = parseFloat(sim.monthly) || 0;
        const simRent = (parseFloat(sim.rent) || 0) * pond;
        const simAmount = parseFloat(sim.amount) || 0;
        const simApport = parseFloat(sim.apport) || 0;

        const futIncome = totalIncomeActuel + simRent;
        const futCharges = totalChargesActuel + simMonthly;
        // Debt ratio includes all fixed monthly charges (credits + charges + new loan)
        const debtFutur = futIncome > 0 ? (futCharges / futIncome * 100) : 0;
        const rvFutur = futIncome - futCharges;
        const cashflow = (parseFloat(sim.rent) || 0) - simMonthly;
        const feasible = debtFutur <= 35 && rvFutur >= minRv;

        return (
          <div key={i} className={`mb-4 p-4 rounded-xl border-2 ${feasible ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}`}>
            <div className="flex items-center justify-between mb-3">
              <input type="text" value={sim.label} onChange={e => updateSim(i, 'label', e.target.value)} className="font-bold text-gray-900 bg-transparent border-none focus:outline-none text-lg" />
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${feasible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{feasible ? 'Realisable' : 'A risque'}</span>
                <button onClick={() => removeSim(i)} className="text-red-400 hover:text-red-600 text-sm">Retirer</button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div><label className="text-xs text-gray-500">Montant projet (EUR)</label><input type="number" value={sim.amount} onChange={e => updateSim(i, 'amount', e.target.value)} className="realease-input text-sm" data-testid={`sim-amount-${i}`} /></div>
              <div><label className="text-xs text-gray-500">Apport (EUR)</label><input type="number" value={sim.apport} onChange={e => updateSim(i, 'apport', e.target.value)} className="realease-input text-sm" data-testid={`sim-apport-${i}`} /></div>
              <div><label className="text-xs text-gray-500">Mensualite credit (EUR)</label><input type="number" value={sim.monthly} onChange={e => updateSim(i, 'monthly', e.target.value)} className="realease-input text-sm" data-testid={`sim-monthly-${i}`} />
                {(() => {
                  const am = calcSimMonthly(sim);
                  if (am == null) return null;
                  const differs = Math.abs(am - simMonthly) > 0.5;
                  return (
                    <p className="text-[10px] mt-0.5 text-gray-500" data-testid={`sim-auto-monthly-${i}`}>
                      Calcul auto : <b className="text-[#008A8D]">{am.toLocaleString('fr-FR')} EUR/mois</b>
                      {sim.monthly_manual && differs && (
                        <button type="button" onClick={() => { const s = [...simulations]; s[i] = { ...s[i], monthly: String(am), monthly_manual: false }; onChange(s); }}
                          className="ml-1.5 text-[#008A8D] underline" data-testid={`sim-apply-auto-${i}`}>appliquer</button>
                      )}
                    </p>
                  );
                })()}
              </div>
              <div><label className="text-xs text-gray-500">Duree (annees)</label><input type="number" value={sim.duration} onChange={e => updateSim(i, 'duration', e.target.value)} className="realease-input text-sm" data-testid={`sim-duration-${i}`} /></div>
              <div><label className="text-xs text-gray-500">Taux (%)</label><input type="number" value={sim.rate} onChange={e => updateSim(i, 'rate', e.target.value)} className="realease-input text-sm" data-testid={`sim-rate-${i}`} /></div>
              <div><label className="text-xs text-gray-500">Loyer attendu (EUR)</label><input type="number" value={sim.rent} onChange={e => updateSim(i, 'rent', e.target.value)} className="realease-input text-sm" /></div>
            </div>

            {simMonthly > 0 && (
              <div className="grid grid-cols-4 gap-3 text-center text-sm">
                <div className="p-2 bg-white/60 rounded-lg">
                  <p className="text-xs text-gray-500">Endettement actuel</p>
                  <p className={`font-bold ${debtActuel > 35 ? 'text-red-600' : debtActuel > 33 ? 'text-orange-500' : 'text-green-600'}`}>{debtActuel.toFixed(1)}%</p>
                </div>
                <div className="p-2 bg-white/60 rounded-lg">
                  <p className="text-xs text-gray-500">Endettement futur</p>
                  <p className={`font-bold ${debtFutur > 35 ? 'text-red-600' : debtFutur > 33 ? 'text-orange-500' : 'text-green-600'}`}>{debtFutur.toFixed(1)}%</p>
                </div>
                <div className="p-2 bg-white/60 rounded-lg">
                  <p className="text-xs text-gray-500">Reste a vivre futur</p>
                  <p className={`font-bold ${rvFutur < minRv ? 'text-red-600' : 'text-green-600'}`}>{rvFutur.toLocaleString('fr-FR')} EUR</p>
                </div>
                <div className="p-2 bg-white/60 rounded-lg">
                  <p className="text-xs text-gray-500">Cashflow mensuel</p>
                  <p className={`font-bold ${cashflow >= 0 ? 'text-green-600' : 'text-red-600'}`}>{cashflow >= 0 ? '+' : ''}{cashflow.toLocaleString('fr-FR')} EUR</p>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <button onClick={addSim} className="w-full py-3 border-2 border-dashed border-[#008A8D]/30 rounded-xl text-[#008A8D] font-medium hover:border-[#008A8D] hover:bg-[#008A8D]/5 transition-colors">
        + Ajouter un scenario de simulation
      </button>
    </div>
  );
};

// Auto debt ratio calculation with bank thresholds
const AutoDebtRatio = ({ form }) => {
  const pond = form.ponderate_rental !== false ? 0.8 : 1;

  // Lead income
  const rentalsListTotal = (form.rentals_list || []).reduce((s, r) => s + (parseFloat(r.rent) || 0), 0);
  const leadIncome = (parseFloat(form.monthly_net_income) || 0) + rentalsListTotal * pond;

  // Co-borrower income (uses new rentals_list, legacy single fields ignored)
  const coRentalsTotal = (form.co_borrower_rentals_list || []).reduce((s, r) => s + (parseFloat(r.rent) || 0), 0);
  // Iter 112 : on inclut TOUJOURS les revenus/charges du co-emprunteur si presents.
  const coIncome = (parseFloat(form.co_borrower_income) || 0) + coRentalsTotal * pond;

  // Investisseurs 3+ : revenus, loyers, credits, charges
  const extras = form.extra_associates || [];
  const extraIncome = extras.reduce((s, a) => s + (parseFloat(a.income) || 0)
    + (a.rentals_list || []).reduce((x, r) => x + (parseFloat(r.rent) || 0), 0) * pond, 0);
  const extraCredits = extras.reduce((s, a) => s + (a.credits_list || []).reduce((x, c) => x + (parseFloat(c.monthly) || 0), 0), 0);
  const extraCharges = extras.reduce((s, a) => s + (a.charges_list || []).reduce((x, c) => x + (parseFloat(c.amount) || 0), 0), 0);

  const totalIncome = leadIncome + coIncome + extraIncome;

  // Lead credits + charges
  const creditMonthly = (form.credits || []).reduce((s, c) => s + (parseFloat(c.monthly) || 0), 0);
  const chargesTotal = (form.charges_list || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

  // Co-borrower credits + charges (new lists) -- inclus inconditionnellement (Iter 112)
  const coCreditMonthly = (form.co_borrower_credits_list || []).reduce((s, c) => s + (parseFloat(c.monthly) || 0), 0);
  const coChargesTotal = (form.co_borrower_charges_list || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

  const totalCredits = creditMonthly + coCreditMonthly + extraCredits;
  const totalChargesOnly = chargesTotal + coChargesTotal + extraCharges;

  const totalCharges = totalCredits + totalChargesOnly;
  const debtRatio = totalIncome > 0 ? (totalCharges / totalIncome) * 100 : 0;
  const debtRatioCreditsOnly = totalIncome > 0 ? (totalCredits / totalIncome) * 100 : 0;
  const remainingToLive = totalIncome - totalCharges;

  // Seuils reste a vivre bancaires (par personne au foyer)
  const children = form.children_count || 0;
  const adults = form.has_co_borrower ? 2 : 1;
  const householdSize = adults + children;
  
  // Seuils bancaires moyens: 700 EUR/adulte + 300 EUR/enfant minimum
  const minRemainingToLive = adults * 700 + children * 300;
  const comfortableRemaining = adults * 1000 + children * 500;

  const rvColor = remainingToLive < minRemainingToLive ? 'text-red-600' : remainingToLive < comfortableRemaining ? 'text-orange-500' : 'text-green-600';
  const rvBg = remainingToLive < minRemainingToLive ? 'bg-red-50 border-red-200' : remainingToLive < comfortableRemaining ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200';
  const rvLabel = remainingToLive < minRemainingToLive ? 'Insuffisant' : remainingToLive < comfortableRemaining ? 'Juste' : 'Confortable';
  const drColor = debtRatio > 35 ? 'text-red-600' : debtRatio > 33 ? 'text-orange-500' : 'text-green-600';

  if (!totalIncome && !totalCredits) return null;

  return (
    <div className={`mt-4 p-4 rounded-lg border ${rvBg}`}>
      <h4 className="font-medium text-gray-900 mb-3">Calcul automatique de l'endettement</h4>
      <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
        <div className="flex justify-between"><span className="text-gray-600">Revenus totaux (locatifs a 80%)</span><span className="font-medium">{totalIncome.toLocaleString('fr-FR')} EUR</span></div>
        <div className="flex justify-between"><span className="text-gray-600">Mensualites credits</span><span className="font-medium">{totalCredits.toLocaleString('fr-FR')} EUR</span></div>
        <div className="flex justify-between"><span className="text-gray-600">Autres charges mensuelles</span><span className="font-medium">{totalChargesOnly.toLocaleString('fr-FR')} EUR</span></div>
        <div className="flex justify-between"><span className="text-gray-600">Foyer ({adults} adulte{adults > 1 ? 's' : ''}{children > 0 ? `, ${children} enfant${children > 1 ? 's' : ''}` : ''})</span><span className="font-medium">{householdSize} personne{householdSize > 1 ? 's' : ''}</span></div>
        <div className="col-span-2 pt-2 border-t border-gray-200 grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Taux d'endettement (credits seuls)</p>
            <p className={`text-lg font-bold ${debtRatioCreditsOnly > 35 ? 'text-red-600' : debtRatioCreditsOnly > 33 ? 'text-orange-500' : 'text-green-600'}`}>{debtRatioCreditsOnly.toFixed(1)}%</p>
            <p className="text-xs text-gray-400">Vue bancaire classique (seuil HCSF 35%)</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Taux d'endettement global (credits + charges)</p>
            <p className={`text-lg font-bold ${drColor}`}>{debtRatio.toFixed(1)}%</p>
            <p className="text-xs text-gray-400">Loyer, assurances, pension inclus</p>
          </div>
        </div>
        <div className="col-span-2 pt-2 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-gray-700 font-medium">Reste a vivre</span>
            <div className="text-right">
              <span className={`text-lg font-bold ${rvColor}`}>{remainingToLive.toLocaleString('fr-FR')} EUR</span>
              <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${remainingToLive < minRemainingToLive ? 'bg-red-100 text-red-700' : remainingToLive < comfortableRemaining ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>{rvLabel}</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">Seuil bancaire minimum pour {householdSize} personne{householdSize > 1 ? 's' : ''} : {minRemainingToLive.toLocaleString('fr-FR')} EUR | Confortable : {comfortableRemaining.toLocaleString('fr-FR')} EUR</p>
        </div>
      </div>
    </div>
  );
};

// Future debt simulation with project
const FField = ({ label, type, value, onChange, opts, testid }) => (
  <div>
    <label className="realease-label">{label}</label>
    {type === 'select' ? (
      <select value={value || ''} onChange={e => onChange(e.target.value)} className="realease-input" data-testid={testid}>{opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}</select>
    ) : <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} className="realease-input" data-testid={testid} />}
  </div>
);

const ScoreBar = ({ label, score }) => {
  const color = score >= 70 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div><div className="flex justify-between text-sm mb-1"><span className="text-gray-600">{label}</span><span className="font-medium">{Math.round(score)}%</span></div>
      <div className="progress-bar"><div className={`progress-bar-fill ${color}`} style={{ width: `${score}%` }}></div></div></div>
  );
};

export { QualificationPage };
export default QualificationDetailPage;
